import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { SaveConfigurationModal } from '../../../components/shared/SaveConfigurationModal';
import { CreatePatientModal } from '../../../components/shared/CreatePatientModal';
import { notifications } from '@mantine/notifications';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, Suspense, useEffect, useCallback } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { usePatients } from '../../../hooks/usePatients';
import { createPatientSafe } from '../../../services/patientManager';
import { useUserDevices } from '../../../hooks/useUserDevices';
import { useAuthUser } from '@store/useAuthStore';
import SelectionCard from '../../../components/shared/SelectionCard';
import SummaryCard from '../../../components/shared/SummaryCard';
import {
  PromptOptionGroup,
  SessionConfiguration,
} from '../../../components/shared/SessionConfiguration';
import { ImportTemplateButton } from './components/ImportTemplateButton';
import {
  EMPTY_SESSION_CONFIG,
  SessionConfigForm,
} from './forms/ExperienceForm/Form';
import {
  ScenarioForm,
  createEmptyScenarioConfig,
} from './forms/ScenarioForm/Form';

import { sessionConfigSchema } from './forms/ExperienceForm/formSchema';
import {
  scenarioConfigSchema,
  type ScenarioConfigFormValues,
} from './forms/ScenarioForm/formSchema';
import {
  SessionCreationProvider,
  SessionCreationStepKey,
  useSessionCreationContext,
  useStepController,
} from './SessionCreationContext';
import { NewSessionPageHeader } from './components/NewSessionPageHeader';
import { SessionCreationStepper } from './components/Stepper';
import { SessionTypeStep } from './components/SessionTypeStep';
import {
  createConfigGeneratedHandler,
  createExperienceSaveHandler,
  createTemplateImportHandler,
  createSessionHandler,
} from './handlers';
import { SessionType } from './types';
import { testOptions } from './constants';
import type { SessionConfig } from './utils/experienceForm/transform';
import { useUserSubscriptionsQuery } from '../../../hooks/queries/useUserSubscriptionsQuery';
import { useActiveProductAssetsQuery } from '../../../hooks/queries/useActiveProductAssetsQuery';
import { PRODUCT_CATEGORY, PRODUCTS_BY_ID } from '../../../constants/products';
import { ActiveProductsOverview } from './components/ActiveProductsOverview';
import { AlertCircle, Loader2 } from 'lucide-react';

export type SessionTypeProductInfo = {
  id: number;
  name: string;
  image: string | null;
  isActive: boolean;
  untilDate: string | null;
  category: string | null;
};

const SCENARIO_PRODUCT_IDS = [75, 76];
const EXPERIENCE_PRODUCT_IDS = [77, 78, 79, 80, 81];
const ALL_SUBSCRIPTION_PRODUCT_IDS = [
  ...SCENARIO_PRODUCT_IDS,
  ...EXPERIENCE_PRODUCT_IDS,
];

function NewSessionContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 1300px)');
  const { steps: sessionSteps, validateAll } = useSessionCreationContext();
  const patientStepValid = sessionSteps.patient.isValid;
  const deviceStepValid = sessionSteps.device.isValid;
  const sessionTypeStepValid = sessionSteps.sessionType.isValid;
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPatient, setSelectedPatient] = useStepController<string>({
    key: 'patient',
  });
  const [selectedDevice, setSelectedDevice] = useStepController<string>({
    key: 'device',
  });
  const [selectedTest] = useState<string | null>(null);
  const [selectedSessionType, setSelectedSessionType] =
    useStepController<SessionType>({
      key: 'sessionType',
      defaultValue: 'esperienza',
    });

  const [experienceConfigId, setExperienceConfigId] = useState<string | null>(
    null
  );
  const [isSavingExperienceConfig, setIsSavingExperienceConfig] =
    useState(false);
  const [isExperienceFormDirty, setIsExperienceFormDirty] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Reset confirmation modal state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  // Save configuration modal state
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveModalInitialName, setSaveModalInitialName] = useState<string>('');
  const [saveModalInitialDescription, setSaveModalInitialDescription] =
    useState<string>('');

  // Create patient modal state
  const [isCreatePatientModalOpen, setIsCreatePatientModalOpen] =
    useState(false);
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
  const [pendingPatientId, setPendingPatientId] = useState<string | null>(null);

  // Get current user
  const currentUser = useAuthUser();
  const userId = currentUser?.id ? String(currentUser.id) : undefined;

  // Fetch patients and devices from API
  const {
    patients,
    loading: patientsLoading,
    error: patientsError,
    refetch: refetchPatients,
  } = usePatients();

  const {
    devices: userDevices,
    isLoading: devicesLoading,
    error: devicesError,
  } = useUserDevices(userId);

  const devicesErrorMessage =
    devicesError instanceof Error
      ? devicesError.message
      : devicesError
        ? String(devicesError)
        : null;

  const devices = useMemo(
    () =>
      userDevices
        .filter(device => device.type === 'vr-headset')
        .map(device => ({
          value: device.id.toString(),
          label: `${device.name || 'N/A'} - ${device.model || 'N/A'} (${device.clientId || 'N/A'})`,
          device,
        })),
    [userDevices]
  );

  const subscriptionsQuery = useUserSubscriptionsQuery();
  const activeAssetsQuery = useActiveProductAssetsQuery(
    ALL_SUBSCRIPTION_PRODUCT_IDS
  );

  const subscriptionProducts = subscriptionsQuery.data?.allProducts ?? [];

  const buildProductInfo = useCallback(
    (productIds: number[]): SessionTypeProductInfo[] =>
      productIds.map(productId => {
        const definition = PRODUCTS_BY_ID[productId];
        const subscription = subscriptionProducts.find(
          product => product.productId === productId
        );

        return {
          id: productId,
          name:
            definition?.name ?? subscription?.name ?? `Prodotto ${productId}`,
          image: subscription?.image ?? null,
          isActive: Boolean(subscription?.isActive),
          untilDate: subscription?.untilDate ?? null,
          category:
            definition?.category ??
            subscription?.category ??
            PRODUCT_CATEGORY.LIFE_EXPERIENCE,
        };
      }),
    [subscriptionProducts]
  );

  const scenarioProducts = useMemo(
    () => buildProductInfo(SCENARIO_PRODUCT_IDS),
    [buildProductInfo]
  );

  const experienceProducts = useMemo(
    () => buildProductInfo(EXPERIENCE_PRODUCT_IDS),
    [buildProductInfo]
  );

  const selectedTypeProducts =
    selectedSessionType === 'scenario' ? scenarioProducts : experienceProducts;

  const hasActiveProductsForSelectedType = selectedTypeProducts.some(
    product => product.isActive
  );

  const productsOverviewTitle =
    selectedSessionType === 'scenario'
      ? 'Contenuti Augmented Life Suite disponibili'
      : 'Contenuti Augmented Life Experience disponibili';

  const productsOverviewLoading =
    subscriptionsQuery.isLoading ||
    subscriptionsQuery.isFetching ||
    activeAssetsQuery.isLoading ||
    activeAssetsQuery.isFetching;

  const selectedProductIdsSet = useMemo(
    () => new Set(selectedTypeProducts.map(product => product.id)),
    [selectedTypeProducts]
  );

  const promptVideoOptions = useMemo<PromptOptionGroup[]>(() => {
    const videoGroups = activeAssetsQuery.videoGroups
      .filter(group => selectedProductIdsSet.has(group.productId))
      .map(group => ({
        group: group.group,
        items: group.items.map(item => ({
          value: item.value,
          label: item.label,
          subtitle: item.subtitle,
          icon: typeof item.icon === 'string' ? item.icon : undefined,
          description: item.meta?.category ?? group.group,
        })),
      }));

    // Add "Nessun video" option at the beginning
    return [
      {
        group: 'Opzioni',
        items: [
          {
            value: '',
            label: 'Nessun video (solo audio)',
          },
        ],
      },
      ...videoGroups,
    ];
  }, [activeAssetsQuery.videoGroups, selectedProductIdsSet]);

  const promptAudioOptions = useMemo<PromptOptionGroup[]>(() => {
    const groups: PromptOptionGroup[] = [];

    activeAssetsQuery.rawProductAssets.forEach(asset => {
      if (!selectedProductIdsSet.has(asset.product.id)) {
        return;
      }

      asset.videos.forEach(video => {
        const audioOptions =
          activeAssetsQuery.audioOptionsByVideoId[video.id] ?? [];

        if (!audioOptions.length) {
          return;
        }

        groups.push({
          group: `${asset.product.name} · ${video.name}`,
          items: audioOptions.map(option => ({
            value: option.value,
            label: option.label,
            subtitle: option.subtitle,
            icon: typeof option.icon === 'string' ? option.icon : undefined,
            description: option.meta
              ? [option.meta.lang?.toUpperCase(), option.meta.type]
                  .filter(Boolean)
                  .join(' · ') || undefined
              : undefined,
            videoId: String(video.id),
          })),
        });
      });
    });

    return groups;
  }, [
    activeAssetsQuery.audioOptionsByVideoId,
    activeAssetsQuery.rawProductAssets,
    selectedProductIdsSet,
  ]);

  const isSubscriptionsLoading =
    subscriptionsQuery.isLoading || subscriptionsQuery.isFetching;

  const isAssetsLoading =
    activeAssetsQuery.isLoading || activeAssetsQuery.isFetching;

  const configDisabledSuspense = useMemo(() => {
    if (isSubscriptionsLoading || isAssetsLoading) {
      return (
        <Group justify='center'>
          <Loader size='sm' />
        </Group>
      );
    }

    if (
      hasActiveProductsForSelectedType ||
      selectedSessionType === 'scenario'
    ) {
      return null;
    }

    return (
      <Alert
        variant='light'
        color='yellow'
        icon={<AlertCircle size={18} />}
        mt='sm'
      >
        {
          "Per configurare un'esperienza attiva almeno un'app Life Experience compatibile."
        }
      </Alert>
    );
  }, [
    hasActiveProductsForSelectedType,
    isAssetsLoading,
    isSubscriptionsLoading,
    selectedSessionType,
  ]);

  // -----------------------------
  // Form principale per la configurazione dell'esperienza
  // -----------------------------
  const scenarioForm = useForm<ScenarioConfigFormValues>({
    resolver: zodResolver(
      scenarioConfigSchema
    ) as Resolver<ScenarioConfigFormValues>,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: createEmptyScenarioConfig(),
  });

  const experienceForm = useForm<SessionConfig>({
    resolver: zodResolver(sessionConfigSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: EMPTY_SESSION_CONFIG,
  });

  // -----------------------------
  // Handle session type change
  // -----------------------------
  useEffect(() => {
    if (selectedSessionType !== 'esperienza') {
      setExperienceConfigId(null);
      setIsExperienceFormDirty(false);
    }
  }, [selectedSessionType]);

  useEffect(() => {
    if (selectedSessionType === 'scenario') {
      const emptyConfig = createEmptyScenarioConfig();
      console.log(
        '[SessionNewPage] Reset scenario form with config:',
        emptyConfig
      );
      scenarioForm.reset(emptyConfig);
      // Trigger validation dopo il reset per assicurarsi che il form sia valido
      // con i valori di default
      void scenarioForm.trigger().then(isValid => {
        console.log(
          '[SessionNewPage] Scenario form validation result:',
          isValid
        );
        const formState = scenarioForm.formState;
        const formValues = scenarioForm.getValues();
        console.log('[SessionNewPage] Scenario form state:', {
          isValid: formState.isValid,
          errors: formState.errors,
          values: formValues,
        });
        // Log dettagliato degli errori
        if (formState.errors && Object.keys(formState.errors).length > 0) {
          console.error(
            '[SessionNewPage] Validation errors:',
            formState.errors
          );
          if (formState.errors.performance) {
            console.error(
              '[SessionNewPage] Performance errors:',
              formState.errors.performance
            );
          }
        }
        // Log dei valori per verificare se i default sono stati impostati
        console.log('[SessionNewPage] Form values:', {
          scenarioType: formValues.scenarioType,
          performance: formValues.performance,
        });
      });
    }
  }, [selectedSessionType, scenarioForm]);

  useEffect(() => {
    const loadingSubscriptions =
      subscriptionsQuery.isLoading || subscriptionsQuery.isFetching;

    if (loadingSubscriptions) {
      return;
    }

    const hasScenarioAccess = scenarioProducts.some(
      product => product.isActive
    );

    if (!hasScenarioAccess && selectedSessionType === 'scenario') {
      setSelectedSessionType('esperienza');
    }
  }, [
    scenarioProducts,
    selectedSessionType,
    setSelectedSessionType,
    subscriptionsQuery.isLoading,
    subscriptionsQuery.isFetching,
  ]);

  //const { loading: scenariosLoading, error: scenariosError } = useScenarios();

  // Mock data for scenarios (static maps like vulcano, stadio, etc.)

  // Test data for step 4 with custom rendering and groups

  // Memoized step completion check
  const isStepCompleted = useCallback(
    (step: number) => {
      switch (step) {
        case 0:
          return patientStepValid;
        case 1:
          return deviceStepValid;
        case 2:
          return sessionTypeStepValid;
        default:
          return false;
      }
    },
    [deviceStepValid, patientStepValid, sessionTypeStepValid]
  );

  // -----------------------------
  // Handle config generation with descriptions
  // -----------------------------
  const handleConfigGenerated = useMemo(() => {
    const baseHandler = createConfigGeneratedHandler({
      experienceForm,
      setExperienceConfigId,
      setIsExperienceFormDirty,
    });

    return (
      config: unknown,
      descriptions: {
        title: string;
        shortDescription: string;
        description: string;
      }
    ) => {
      baseHandler(config, descriptions);
      // Open save modal with pre-populated name and description
      setSaveModalInitialName(descriptions.title || '');
      setSaveModalInitialDescription(descriptions.description || '');
      setIsSaveModalOpen(true);
    };
  }, [experienceForm, setExperienceConfigId, setIsExperienceFormDirty]);

  // -----------------------------
  // Handle import template confirmation
  // -----------------------------
  const handleImportTemplateConfirm = useMemo(
    () =>
      createTemplateImportHandler({
        experienceForm,
        selectedSessionType: selectedSessionType ?? 'esperienza',
        setSelectedSessionType,
        setActiveStep,
        setExperienceConfigId,
        setIsExperienceFormDirty,
        onTemplateImported: template => {
          // Populate modal fields with template name and description
          setSaveModalInitialName(template.name || '');
          setSaveModalInitialDescription(template.description || '');
        },
      }),
    [
      experienceForm,
      selectedSessionType,
      setSelectedSessionType,
      setActiveStep,
      setExperienceConfigId,
      setIsExperienceFormDirty,
    ]
  );
  // -----------------------------
  // Reset confirmation functions
  // -----------------------------
  const handleResetClick = () => {
    setIsResetModalOpen(true);
  };

  const handleConfirmReset = () => {
    experienceForm.reset(EMPTY_SESSION_CONFIG);
    void experienceForm.trigger();
    setExperienceConfigId(null);
    setIsExperienceFormDirty(false);
    setIsResetModalOpen(false);
  };

  const handleCancelReset = () => {
    setIsResetModalOpen(false);
  };
  // -----------------------------
  // Handle create patient
  // -----------------------------
  const handleCreatePatient = async (code: string) => {
    setIsCreatingPatient(true);
    try {
      const createdPatient = await createPatientSafe(code);

      // Refetch patients list to get the newly created patient
      await refetchPatients();

      if (createdPatient) {
        // Store the patient ID to select after refetch
        const patientId = String(createdPatient.user_id || createdPatient.id);
        setPendingPatientId(patientId);
      } else {
        // If we don't have patient info, set a flag to select the last patient
        // (assuming it's the newest one)
        setPendingPatientId('LAST');
      }
    } catch (error) {
      console.error('Error creating patient:', error);
      setPendingPatientId(null);

      // Check if it's a duplicate error
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isDuplicateError =
        errorMessage.toLowerCase().includes('duplicate') ||
        errorMessage.toLowerCase().includes('duplicato');

      if (isDuplicateError) {
        // Show toast for duplicate error
        notifications.show({
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: 'Errore',
          autoClose: 5000,
          message: errorMessage,
        });
      } else {
        // Re-throw other errors to be handled by the modal
        throw error;
      }
    } finally {
      setIsCreatingPatient(false);
    }
  };

  // Effect to select the newly created patient after refetch
  useEffect(() => {
    if (pendingPatientId && patients.length > 0) {
      if (pendingPatientId === 'LAST') {
        // Select the last patient (assuming it's the newest)
        const lastPatient = patients[patients.length - 1];
        if (lastPatient) {
          setSelectedPatient(lastPatient.value);
        }
        setPendingPatientId(null);
      } else {
        // Try to find the patient by ID
        const newPatient = patients.find(p => p.value === pendingPatientId);
        if (newPatient) {
          setSelectedPatient(newPatient.value);
          setPendingPatientId(null);
        } else {
          // If not found by ID, try to select the last patient (assuming it's the newest)
          // This is a fallback in case the ID doesn't match
          const lastPatient = patients[patients.length - 1];
          if (lastPatient) {
            setSelectedPatient(lastPatient.value);
          }
          setPendingPatientId(null);
        }
      }
    }
  }, [patients, pendingPatientId, setSelectedPatient]);
  // -----------------------------

  // Memoized selected device data
  const selectedDeviceData = useMemo(() => {
    const found = devices.find(d => d.value === selectedDevice);
    console.log('[SessionNewPage] Debug selectedDeviceData:', {
      selectedDevice,
      devicesLength: devices.length,
      found,
      userDevicesLength: userDevices.length,
      userDevices: userDevices,
    });
    return found;
  }, [devices, selectedDevice, userDevices]);

  // Memoized summary data
  const summaryData = useMemo(() => {
    const patientLabel = patients.find(p => p.value === selectedPatient)?.label;
    const deviceLabel = devices.find(d => d.value === selectedDevice)?.label;

    return { patientLabel, deviceLabel };
  }, [
    selectedPatient,
    selectedDevice,
    selectedTest,
    patients,
    devices,
    testOptions,
  ]);

  // Memoized summary items
  const summaryItems = useMemo(
    () => [
      { label: 'Utente', value: summaryData.patientLabel, color: 'blue' },
      { label: 'Visore', value: summaryData.deviceLabel, color: 'green' },
    ],
    [summaryData]
  );

  // -----------------------------
  // Handle save experience configuration
  // -----------------------------
  const saveHandler = useMemo(
    () =>
      createExperienceSaveHandler({
        currentUserId: currentUser?.id,
        selectedSessionType: selectedSessionType ?? 'esperienza',
        experienceConfigId,
        experienceForm,
        setExperienceConfigId,
        setIsExperienceFormDirty,
        setIsSavingExperienceConfig,
      }),
    [
      currentUser?.id,
      selectedSessionType,
      experienceConfigId,
      experienceForm,
      setExperienceConfigId,
      setIsExperienceFormDirty,
      setIsSavingExperienceConfig,
    ]
  );

  const handleSaveExperienceConfiguration = () => {
    // Only open modal for experience, not for scenario
    if (selectedSessionType !== 'esperienza') {
      return;
    }
    const formValues = experienceForm.getValues();
    // Open modal to get name and description
    setSaveModalInitialName(formValues.name || '');
    setSaveModalInitialDescription(formValues.description || '');
    setIsSaveModalOpen(true);
  };

  const handleSaveFromModal = async (name: string, description: string) => {
    const formValues = experienceForm.getValues();
    await saveHandler(formValues, name, description);
  };

  const invalidStepNotifications = useMemo<
    Record<
      SessionCreationStepKey,
      { color: string; title: string; message: string }
    >
  >(
    () => ({
      patient: {
        color: 'yellow',
        title: t('session_validation_patient_title', 'Attenzione'),
        message: t(
          'session_validation_patient_message',
          'Seleziona un utente prima di procedere'
        ),
      },
      device: {
        color: 'yellow',
        title: t('session_validation_device_title', 'Attenzione'),
        message: t(
          'session_validation_device_message',
          'Seleziona un visore prima di salvare'
        ),
      },
      sessionType: {
        color: 'yellow',
        title: t('session_validation_type_title', 'Attenzione'),
        message: t(
          'session_validation_type_message',
          'Seleziona un tipo di sessione prima di salvare'
        ),
      },
      configuration: {
        color: 'yellow',
        title: t('session_validation_config_title', 'Attenzione'),
        message: t(
          'session_validation_config_message',
          'Completa la configurazione della sessione prima di procedere'
        ),
      },
    }),
    [t]
  );

  // -----------------------------
  // Handle create session
  // -----------------------------
  const handleCreateSession = useMemo(() => {
    const handler = createSessionHandler({
      currentUserId: currentUser?.id?.toString() ?? null,
      selectedPatientId: selectedPatient ?? null,
      selectedDeviceId: selectedDevice ?? null,
      selectedSessionType: selectedSessionType ?? null,
      experienceForm,
      scenarioForm,
      validateAll,
      invalidStepNotifications,
      setActiveStep,
      navigate,
      queryClient,
      allDevices: userDevices,
    });

    return async () => {
      setIsCreatingSession(true);
      try {
        await handler();
      } finally {
        setIsCreatingSession(false);
      }
    };
  }, [
    currentUser?.id,
    selectedPatient,
    selectedDevice,
    selectedSessionType,
    experienceForm,
    scenarioForm,
    invalidStepNotifications,
    navigate,
    queryClient,
    setActiveStep,
    validateAll,
    userDevices,
  ]);

  const isExperienceUpdateMode = Boolean(experienceConfigId);
  const experienceSaveButtonDisabled =
    isExperienceUpdateMode && !isExperienceFormDirty;
  const experienceSaveButtonHint = experienceSaveButtonDisabled
    ? 'Configurazione salvata'
    : undefined;

  return (
    <Stack gap='lg'>
      <NewSessionPageHeader />

      <Card shadow='sm' withBorder p={isMobile ? 'md' : 'xl'}>
        <Stack gap='lg'>
          <Flex gap='xl' align='flex-start'>
            {/* Stepper Verticale a Sinistra - Nascosto su mobile */}
            {!isMobile && (
              <div style={{ minWidth: '300px' }}>
                <SessionCreationStepper
                  active={activeStep}
                  onStepClick={setActiveStep}
                  patientStepValid={isStepCompleted(0) ? true : false}
                  deviceStepValid={isStepCompleted(1) ? true : false}
                  sessionTypeStepValid={isStepCompleted(2) ? true : false}
                />
              </div>
            )}

            {/* Contenuto degli Step a Destra */}
            <Box w='100%'>
              <Stack gap='lg'>
                {/* Step 1: Selezione Utente */}
                <SelectionCard
                  title='1. Selezione Utente'
                  description="Seleziona l'utente che parteciperà alla sessione VR"
                  label='Utente'
                  placeholder='Seleziona un utente'
                  data={patients}
                  value={selectedPatient}
                  onChange={setSelectedPatient}
                  loading={patientsLoading}
                  error={patientsError}
                  isActive={activeStep === 0}
                  disabled={isCreatingSession}
                  rightAction={
                    <Button
                      variant='light'
                      size='sm'
                      onClick={() => setIsCreatePatientModalOpen(true)}
                      disabled={isCreatingSession || patientsLoading}
                    >
                      Aggiungi Utente
                    </Button>
                  }
                />

                {/* Step 2: Selezione Visore */}
                <SelectionCard
                  title='2. Selezione Visore'
                  description='Seleziona il visore VR che verrà utilizzato per la sessione'
                  label='Dispositivo VR'
                  placeholder='Seleziona un visore'
                  data={devices}
                  value={selectedDevice}
                  onChange={setSelectedDevice}
                  loading={devicesLoading}
                  error={devicesErrorMessage}
                  isActive={activeStep === 1}
                  showDeviceInfo={true}
                  selectedDeviceData={selectedDeviceData}
                  allDevices={userDevices}
                  disabled={isCreatingSession}
                />

                {/* Step 3: Configura Sessione */}
                <SessionTypeStep
                  setActiveStep={setActiveStep}
                  activeStep={activeStep}
                  selectedSessionType={selectedSessionType}
                  setSelectedSessionType={type => {
                    setSelectedSessionType(type);
                    setActiveStep(3);
                  }}
                  scenarioProducts={scenarioProducts}
                  experienceProducts={experienceProducts}
                  subscriptionsLoading={
                    subscriptionsQuery.isLoading ||
                    subscriptionsQuery.isFetching
                  }
                />
                {selectedSessionType && (
                  <ActiveProductsOverview
                    sessionType={selectedSessionType}
                    assets={activeAssetsQuery}
                    products={selectedTypeProducts}
                    loading={productsOverviewLoading}
                    title={productsOverviewTitle}
                  />
                )}
                {selectedSessionType && (
                  <SessionConfiguration
                    sessionType={selectedSessionType}
                    activeStep={activeStep}
                    onReset={handleResetClick}
                    importTemplateTrigger={
                      <ImportTemplateButton
                        onImport={handleImportTemplateConfirm}
                      />
                    }
                    onConfigGenerated={handleConfigGenerated}
                    saveButtonLoading={isSavingExperienceConfig}
                    saveButtonDisabled={experienceSaveButtonDisabled}
                    saveButtonHint={experienceSaveButtonHint}
                    isConfigEnabled={
                      selectedSessionType === 'scenario'
                        ? true
                        : hasActiveProductsForSelectedType
                    }
                    configDisabledContent={
                      selectedSessionType === 'scenario'
                        ? undefined
                        : configDisabledSuspense
                    }
                    promptVideoOptions={promptVideoOptions}
                    promptAudioOptions={promptAudioOptions}
                    isFormDisabled={isCreatingSession}
                    configForm={
                      selectedSessionType === 'scenario' ? (
                        <FormProvider {...scenarioForm}>
                          <ScenarioForm
                            isPerformanceActive={
                              scenarioProducts.find(p => p.id === 76)
                                ?.isActive ?? false
                            }
                            isExposureActive={
                              scenarioProducts.find(p => p.id === 75)
                                ?.isActive ?? false
                            }
                            isBodyImageActive
                          />
                        </FormProvider>
                      ) : (
                        <FormProvider {...experienceForm}>
                          <SessionConfigForm
                            onSubmit={handleSaveExperienceConfiguration}
                            onDirtyChange={setIsExperienceFormDirty}
                          />
                        </FormProvider>
                      )
                    }
                    onSubmitForm={() => {
                      // Open save modal (handleSaveExperienceConfiguration opens modal)
                      if (isSavingExperienceConfig) {
                        return;
                      }
                      handleSaveExperienceConfiguration();
                    }}
                  />
                )}

                {/* Riepilogo */}
                <SummaryCard
                  title='Riepilogo Configurazione:'
                  items={summaryItems}
                  isVisible={activeStep === 4}
                />
              </Stack>
            </Box>
          </Flex>

          <Divider />

          {/* Navigation Buttons */}
          <Suspense fallback={<div>Loading navigation...</div>}>
            <Group justify='flex-end'>
              {(() => {
                const isFormValid =
                  selectedSessionType === 'esperienza'
                    ? experienceForm.formState.isValid
                    : selectedSessionType === 'scenario'
                      ? scenarioForm.formState.isValid
                      : true; // Se non c'è tipo selezionato, non bloccare

                const scenarioErrors = scenarioForm.formState.errors;
                const scenarioValues = scenarioForm.getValues();
                console.log('[SessionNewPage] Button state check:', {
                  selectedSessionType,
                  isCreatingSession,
                  isFormValid,
                  experienceFormIsValid: experienceForm.formState.isValid,
                  scenarioFormIsValid: scenarioForm.formState.isValid,
                  scenarioFormValues: scenarioValues,
                  scenarioFormErrors: scenarioErrors,
                  buttonDisabled: isCreatingSession || !isFormValid,
                });
                if (scenarioErrors && Object.keys(scenarioErrors).length > 0) {
                  console.error(
                    '[SessionNewPage] Scenario form validation errors:',
                    scenarioErrors
                  );
                  if (scenarioErrors.performance) {
                    console.error(
                      '[SessionNewPage] Performance field errors:',
                      scenarioErrors.performance
                    );
                  }
                }
                if (scenarioValues.performance) {
                  console.log(
                    '[SessionNewPage] Performance values:',
                    scenarioValues.performance
                  );
                }

                return (
                  <Button
                    onClick={handleCreateSession}
                    loading={isCreatingSession}
                    disabled={isCreatingSession || !isFormValid}
                    leftSection={
                      isCreatingSession ? <Loader2 size={16} /> : undefined
                    }
                  >
                    {isCreatingSession
                      ? 'Salvataggio in corso'
                      : t('create_session', 'Crea Sessione')}
                  </Button>
                );
              })()}
            </Group>
          </Suspense>
        </Stack>
      </Card>

      {/* Reset Confirmation Modal */}
      <Modal
        opened={isResetModalOpen}
        onClose={handleCancelReset}
        title='Conferma Reset'
        centered
        size='sm'
      >
        <Stack gap='md'>
          <Text size='sm'>
            Sei sicuro di voler resettare tutti gli step condizionali?
          </Text>
          <Text size='xs' c='dimmed'>
            Questa azione cancellerà permanentemente tutti gli step creati e non
            può essere annullata.
          </Text>

          <Group justify='flex-end' gap='sm'>
            <Button variant='light' onClick={handleCancelReset}>
              Annulla
            </Button>
            <Button color='red' onClick={handleConfirmReset}>
              Conferma il Reset
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Save Configuration Modal */}
      <SaveConfigurationModal
        opened={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={handleSaveFromModal}
        initialName={saveModalInitialName}
        initialDescription={saveModalInitialDescription}
        loading={isSavingExperienceConfig}
      />

      {/* Create Patient Modal */}
      <CreatePatientModal
        opened={isCreatePatientModalOpen}
        onClose={() => setIsCreatePatientModalOpen(false)}
        onCreate={handleCreatePatient}
        loading={isCreatingPatient}
      />
    </Stack>
  );
}

export function SessionNewPage() {
  return (
    <SessionCreationProvider>
      <NewSessionContent />
    </SessionCreationProvider>
  );
}
